import { useState } from "react";
import type { Candidate } from "../types";

function scoreColor(score: number): string {
  if (score >= 80) return "text-emerald-600";
  if (score >= 60) return "text-lime-600";
  if (score >= 40) return "text-yellow-500";
  if (score >= 20) return "text-orange-500";
  return "text-[#ff0000]";
}

function barColor(score: number): string {
  if (score >= 80) return "#059669";
  if (score >= 60) return "#65a30d";
  if (score >= 40) return "#eab308";
  if (score >= 20) return "#f97316";
  return "#ff0000";
}

const recStyles: Record<Candidate["recommendation"], string> = {
  strong_hire: "bg-emerald-100 text-emerald-800",
  consider: "bg-amber-100 text-amber-800",
  reject: "bg-rose-100 text-error-text",
};

const recLabel: Record<Candidate["recommendation"], string> = {
  strong_hire: "Strong hire",
  consider: "Consider",
  reject: "Reject",
};

const ringColor: Record<Candidate["recommendation"], string> = {
  strong_hire: "#059669",
  consider: "#d97706",
  reject: "#e11d48",
};

function ScoreRing({ score, recommendation }: { score: number; recommendation: Candidate["recommendation"] }) {
  const size = 72;
  const stroke = 5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f2f2f2" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={ringColor[recommendation]} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold tabular-nums text-near-black">{Math.round(score)}</span>
      </div>
    </div>
  );
}

function StarRating({ score }: { score: number }) {
  const stars = Math.round((score / 100) * 5 * 2) / 2;
  const full = Math.floor(stars);
  const half = stars - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const fillColor = stars >= 4 ? "#facc15" : stars >= 2 ? "#f97316" : "#ef4444";
  const halfGradientId = `half-star-${Math.round(score)}`;
  const starPath = "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";

  return (
    <div className="flex gap-0.5">
      {Array.from({ length: full }).map((_, i) => (
        <svg key={`f${i}`} className="h-4 w-4" viewBox="0 0 20 20" fill={fillColor}><path d={starPath} /></svg>
      ))}
      {half && (
        <svg className="h-4 w-4" viewBox="0 0 20 20">
          <defs><linearGradient id={halfGradientId}><stop offset="50%" stopColor={fillColor} /><stop offset="50%" stopColor="#e5e7eb" /></linearGradient></defs>
          <path fill={`url(#${halfGradientId})`} d={starPath} />
        </svg>
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <svg key={`e${i}`} className="h-4 w-4" viewBox="0 0 20 20" fill="#e5e7eb"><path d={starPath} /></svg>
      ))}
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr + "Z").getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;

  return new Date(dateStr + "Z").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fullDate(dateStr: string): string {
  return new Date(dateStr + "Z").toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CandidateCard({
  candidate,
  onArchive,
  onDelete,
}: {
  candidate: Candidate;
  onArchive?: (id: number, archived: boolean) => void;
  onDelete?: (id: number) => void;
}) {
  const isArchived = candidate.archived;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-card bg-white shadow-card transition-shadow hover:shadow-card-hover">
      {/* Header — always visible, clickable to expand/collapse */}
      <div
        className={`cursor-pointer p-5 ${isArchived ? "opacity-50" : ""}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <ScoreRing score={candidate.match_score} recommendation={candidate.recommendation} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-[22px] font-semibold leading-tight tracking-[-0.44px] text-near-black">
                    {candidate.name || "Unknown candidate"}
                  </h3>
                  <span id="recommendation-badge"
                    className={`inline-flex rounded-badge px-3 py-1 text-[11px] font-semibold leading-tight ${recStyles[candidate.recommendation]}`}
                  >
                    {recLabel[candidate.recommendation]}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-text-secondary">
                  {candidate.filename || "—"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {onArchive && (
                  <button
                    onClick={() => onArchive(candidate.id, !isArchived)}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
                    title={isArchived ? "Unarchive" : "Archive"}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      {isArchived ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15M9 12l3 3m0 0 3-3m-3 3V2.25" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                      )}
                    </svg>
                    {isArchived ? "Unarchive" : "Archive"}
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(candidate.id)}
                    className="flex items-center gap-1 rounded-btn px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
                    title="Delete permanently"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3">
              <StarRating score={candidate.match_score} />
              <p className="text-[11px] font-semibold text-border-card" title={fullDate(candidate.created_at)}>
                Screened · {timeAgo(candidate.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Expandable details */}
      {expanded && (
        <div className={`border-t border-surface-secondary px-8 pb-5 ${isArchived ? "opacity-50" : ""}`}>
          <div className="mt-4 flex items-start justify-between gap-6">
            <div id="candidate-details" className="flex flex-col gap-0.5 text-sm text-text-secondary">
              {candidate.email && (
                <span><span className="font-medium text-text-focused">Email:</span> {candidate.email}</span>
              )}
              {candidate.phone && (
                <span><span className="font-medium text-text-focused">Phone:</span> {candidate.phone}</span>
              )}
              {candidate.years_experience != null && (
                <span><span className="font-medium text-text-focused">Experience:</span> {candidate.years_experience} years</span>
              )}
              {candidate.education && (
                <span><span className="font-medium text-text-focused">Education:</span> {candidate.education}</span>
              )}
            </div>
            {candidate.photo && (
              <img
                src={candidate.photo}
                alt={candidate.name || "Candidate"}
                className="h-24 w-24 shrink-0 rounded-card object-cover shadow-card"
              />
            )}
          </div>

          {candidate.summary && (
            <p className="mt-4 text-sm leading-[1.43] text-text-focused">{candidate.summary}</p>
          )}

          {candidate.skills?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {candidate.skills.map((s) => (
                <span
                  key={s}
                  className="rounded-btn bg-surface-secondary px-2.5 py-0.5 text-xs font-medium text-near-black"
                >
                  {s}
                </span>
              ))}
            </div>
          )}

          {candidate.matrix_breakdown && candidate.matrix_breakdown.length > 0 && (
            <div className="mt-4 space-y-3 border-t border-surface-secondary pt-4">
              <div className="text-[8px] font-bold uppercase tracking-[0.32px] text-text-secondary">
                Hiring matrix breakdown
              </div>
              {candidate.matrix_breakdown.map((b) => (
                <div key={b.criterion} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-near-black">{b.criterion}</span>
                    <span className="tabular-nums">
                      <span className={`text-base font-bold ${scoreColor(b.score)}`}>{Math.round(b.score)}</span>
                      <span className="text-xs text-text-secondary"> / 100</span>
                      <span className="ml-1 text-xs text-text-secondary">(w {b.weight})</span>
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-secondary">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, b.score))}%`, backgroundColor: barColor(b.score) }}
                    />
                  </div>
                  {b.notes && (
                    <p className="text-xs leading-[1.43] text-text-secondary">{b.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
